import { type AppIcons } from '@backrs_ui/common-ui';

import standardizedTestsBanner from '@backrs_ui/grow-ui/src/assets/images/standardized-tests-guide/standardized-tests-banner.png';
import {
  GuideFaqSection,
  GuideMythsAndMistakesSection,
  GuidePageShell,
  GuideSections,
  GuidePrinciplesGrid,
  GuideStrategyByGoalSection,
  GuideToolsSection,
} from '@backrs_ui/grow-ui/src/components/GuidePage';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import { StickerCardId } from '@backrs_ui/grow-ui/src/pages/Home/hooks/stickerCardMapping';

import { MultiYearFrameworkSection, WhyScoresMatterSection } from './components';
import { useAgentContext } from './StandardizedTestsGuidePageContainer.agentContext';
import { standardizedTestsContent } from '../content';

const STRATEGY_ICONS: Record<string, AppIcons> = {
  'college-admissions': 'GraduationHat02',
  'ap-ib': 'BookOpen01',
  scholarship: 'Award03',
  cte: 'Tool02',
};

const SECTION_IDS = {
  overview: 'standardized-tests-section-overview',
  whyScoresMatter: 'standardized-tests-section-why-scores-matter',
  tools: 'standardized-tests-section-tools',
  corePrinciples: 'standardized-tests-section-core-principles',
  myths: 'standardized-tests-section-myths',
  strategy: 'standardized-tests-section-strategy',
  multiYearFramework: 'standardized-tests-section-multi-year-framework',
  faq: 'standardized-tests-section-faq',
} as const;

const STANDARDIZED_TESTS_TOOL_ID = 'standardized_tests_guide';

const STANDARDIZED_TESTS_TOOL_IDS: readonly StickerCardId[] = [
  'are_you_on_track',
  'improve_test_scores',
  'what_are_your_chances',
];

export const StandardizedTestsGuidePageContainer = () => {
  useAgentContext();

  const sections = [
    {
      id: SECTION_IDS.tools,
      title: standardizedTestsContent.tools.title,
      subtitle: standardizedTestsContent.tools.subtitle,
      rightSlot: (
        <LubavButton
          id="standardized-tests-tools-lubav-btn"
          label="Which tool should I start with?"
          prompt="Which of these tools should I start with to support my standardized test preparation?"
          allowTools={false}
        />
      ),
      content: <GuideToolsSection toolIds={STANDARDIZED_TESTS_TOOL_IDS} />,
    },
    {
      id: SECTION_IDS.whyScoresMatter,
      title: standardizedTestsContent.whyScoresMatter.title,
      subtitle: standardizedTestsContent.whyScoresMatter.subtitle,
      rightSlot: (
        <LubavButton
          id="standardized-tests-why-scores-lubav-btn"
          label="How does this apply to me?"
          prompt="Based on my profile and goals, which of these benefits of strong test scores matters most for me right now? Consider scholarships, placement/credit, program eligibility, academic preparation, and self-knowledge."
          allowTools={false}
        />
      ),
      content: <WhyScoresMatterSection content={standardizedTestsContent.whyScoresMatter} />,
    },
    {
      id: SECTION_IDS.corePrinciples,
      title: standardizedTestsContent.corePrinciples.title,
      subtitle: standardizedTestsContent.corePrinciples.subtitle,
      rightSlot: (
        <LubavButton
          id="standardized-tests-principles-lubav-btn"
          label="Which matters most for me?"
          prompt="Based on what you know about me, which of the six core principles for test preparation should I focus on most: (1) Know Your Targets, (2) Plan Ahead, (3) Focus on Mastery, (4) Customize the Plan, (5) Practice Under Real Conditions, or (6) Use the Full Toolkit? Explain why and give me one actionable step."
          allowTools={false}
        />
      ),
      content: (
        <GuidePrinciplesGrid
          principles={standardizedTestsContent.corePrinciples.principles}
          columnFlow
        />
      ),
    },
    {
      id: SECTION_IDS.myths,
      title: standardizedTestsContent.mythsAndMistakes.title,
      subtitle: standardizedTestsContent.mythsAndMistakes.subtitle,
      rightSlot: (
        <LubavButton
          id="standardized-tests-myths-lubav-btn"
          label="Discuss with Lubav"
          prompt="I just read the Standardized Tests Myths & Mistakes section. Based on what you know about me, identify which myth or mistake I'm most at risk for and explain why. Then tell me what I should do differently."
          allowTools={false}
        />
      ),
      content: (
        <GuideMythsAndMistakesSection
          intro={standardizedTestsContent.mythsAndMistakes.intro}
          mythLabel={standardizedTestsContent.mythsAndMistakes.mythLabel}
          mistakeLabel={standardizedTestsContent.mythsAndMistakes.mistakeLabel}
          items={standardizedTestsContent.mythsAndMistakes.items}
        />
      ),
    },
    {
      id: SECTION_IDS.strategy,
      title: standardizedTestsContent.strategyByGoal.title,
      subtitle: standardizedTestsContent.strategyByGoal.subtitle,
      rightSlot: (
        <LubavButton
          id="standardized-tests-strategy-lubav-btn"
          label="Which strategy fits me?"
          prompt="Based on my profile, goals, and intended path, which testing strategy fits me best: college admissions, AP/IB focused, scholarship-driven, or career/technical? Give me personalized advice for my situation."
        />
      ),
      content: (
        <GuideStrategyByGoalSection
          strategies={standardizedTestsContent.strategyByGoal.strategies}
          iconMap={STRATEGY_ICONS}
          columns={2}
        />
      ),
    },
    {
      id: SECTION_IDS.multiYearFramework,
      title: standardizedTestsContent.multiYearFramework.title,
      subtitle: standardizedTestsContent.multiYearFramework.subtitle,
      rightSlot: (
        <LubavButton
          id="standardized-tests-framework-lubav-btn"
          label="What should I do now?"
          prompt="Based on my grade level and current situation, what should I be doing right now in my testing timeline? Give me specific, actionable steps for this phase of my preparation."
          allowTools={false}
        />
      ),
      content: <MultiYearFrameworkSection content={standardizedTestsContent.multiYearFramework} />,
    },
    {
      id: SECTION_IDS.faq,
      title: standardizedTestsContent.faq.title,
      subtitle: standardizedTestsContent.faq.subtitle,
      rightSlot: (
        <LubavButton
          id="standardized-tests-faq-lubav-btn"
          label="Ask a question"
          prompt="I'm on the Standardized Tests Guide FAQ page. Based on what you know about me and my academic situation, what's the most important question I should be asking about standardized testing right now? Answer it for me with specific advice."
          allowTools={false}
        />
      ),
      content: (
        <GuideFaqSection
          idPrefix="standardized-tests-faq"
          questions={standardizedTestsContent.faq.questions}
        />
      ),
    },
  ];

  return (
    <GuidePageShell
      toolId={STANDARDIZED_TESTS_TOOL_ID}
      banner={{
        src: standardizedTestsBanner,
        alt: 'How to Win at Standardized Tests',
      }}
    >
      <GuideSections
        header={standardizedTestsContent.header}
        overview={{
          id: SECTION_IDS.overview,
        }}
        sections={sections}
      />
    </GuidePageShell>
  );
};
